import type { Player as ShakaPlayer } from 'shaka-player/dist/shaka-player.dash.js'

import { CLOUDFLARE_CUSTOMER_URL } from '@/constants/general'
import type { OutOfPageAdType, PageAdSize } from '@/modules/adv'
import { OutOfPageBanner } from '@/modules/adv/out-of-page-banner'
import { AdProviderNames } from '@/types'
import type { EventForLoggersType } from '@/types/logger.types'

import { locales } from './locales'
import { WrapperControls, renderInstreamTemplate } from './template/instream-template'
import type { Creative } from './types'

type InstreamSupport = 'no' | 'muted' | 'full'

function getStreamUrlPrefix(id: string): string {
    return `${CLOUDFLARE_CUSTOMER_URL}${id}/manifest`
}

const UNSKIPPABLE_DURATION_SEC = 5
const MICRO_TIMEOUT = 16

export class VideoBanner {
    ready: Promise<void>

    private readonly logEvent: (event: EventForLoggersType) => void
    private readonly getAdUnit: (type: OutOfPageAdType, creative?: Creative) => string
    private readonly sendRequest: (url: string | undefined) => void
    private readonly getCreative: (type: OutOfPageAdType, sizes?: PageAdSize[]) => Creative | null
    private readonly locale: keyof typeof locales

    private readonly wrapperControls: WrapperControls
    private readonly videoWrapper: HTMLDivElement
    private readonly videoPlayer: HTMLVideoElement
    private readonly skipButton: HTMLButtonElement
    private readonly learnMoreButton: HTMLButtonElement

    private readonly sampleVideoSrc: string
    private learnMoreClickHandler?: () => void
    private videoClickHandler?: () => void
    private isMuted = false
    private shakaPlayerRef: ShakaPlayer | null = null

    constructor({
        logEvent,
        getAdUnit,
        sendRequest,
        getCreative,
        footerHeight,
        locale,
    }: {
        logEvent: (event: EventForLoggersType) => void
        getAdUnit: (type: OutOfPageAdType, creative?: Creative) => string
        sendRequest: (url: string | undefined) => void
        getCreative: (type: OutOfPageAdType, sizes?: PageAdSize[]) => Creative | null
        footerHeight: string | undefined
        locale: string
    }) {
        this.logEvent = logEvent
        this.getAdUnit = getAdUnit
        this.sendRequest = sendRequest
        this.getCreative = getCreative
        this.locale = locales[locale as keyof typeof locales] ? (locale as keyof typeof locales) : 'en-US'

        try {
            const { wrapper, controls } = renderInstreamTemplate({
                footerHeight,
                locale: this.locale,
            })
            document.body.insertAdjacentElement('beforeend', wrapper)
            this.wrapperControls = controls
            this.videoWrapper = wrapper
        } catch (e) {
            throw new Error(`Cannot create the instream wrapper app due to error: ${e}`)
        }

        this.videoPlayer = this.videoWrapper.querySelector('[data-id="instream-player"]') as HTMLVideoElement
        this.skipButton = this.videoWrapper.querySelector('[data-id="skip-button"]') as HTMLButtonElement
        this.learnMoreButton = this.videoWrapper.querySelector('[data-id="learn-more-button"]') as HTMLButtonElement

        this.sampleVideoSrc = this.videoPlayer.querySelector('source')?.src || ''

        this.ready = this.loadShakaPlayer()
    }

    private async loadShakaPlayer(): Promise<void> {
        if (this.videoPlayer.canPlayType('application/vnd.apple.mpegurl') === '') {
            const shaka = await import('shaka-player/dist/shaka-player.dash.js')
            this.shakaPlayerRef = new shaka.Player()
        }
    }

    private async playVideo(banner: OutOfPageBanner, creative: Creative, type: OutOfPageAdType): Promise<void> {
        try {
            this.skipButton.disabled = true
            this.skipButton.style.display = creative.skippable ? 'block' : 'none'
            this.skipButton.textContent = `${UNSKIPPABLE_DURATION_SEC}`

            let skipButtonAppearLogged = false

            this.learnMoreClickHandler = () => {
                this.videoPlayer.pause()
                this.wrapperControls.switchOverlayVisibility(true)

                this.logEvent({
                    event: 'direct_ad_event',
                    eventName: 'direct_ad_click',
                    adPlacement: type,
                    adUnit: this.getAdUnit(type, creative),
                })

                window.open(`/redirect.html?url=${encodeURIComponent(creative.clickUrl)}`, '_blank')
            }

            this.learnMoreButton.addEventListener('click', this.learnMoreClickHandler)

            this.videoClickHandler = () => {
                this.videoPlayer.pause()
                this.wrapperControls.switchOverlayVisibility(true)
            }
            this.videoPlayer.addEventListener('click', this.videoClickHandler)

            await this.videoPlayer.play()
            banner.triggerViewable()

            this.logEvent({
                event: 'direct_ad_event',
                eventName: 'direct_ad_impression',
                adPlacement: type,
                adUnit: this.getAdUnit(type, creative),
            })

            this.sendRequest(creative.impressionTrackingUrl)

            this.videoPlayer.addEventListener('ended', () => {
                this.endAd(banner, type, creative)
            })

            this.videoPlayer.addEventListener('timeupdate', () => {
                const { currentTime } = this.videoPlayer

                const timeToSkip = Math.round(UNSKIPPABLE_DURATION_SEC - currentTime)
                banner.triggerTimer(Math.round(this.videoPlayer.duration - currentTime))

                if (creative.skippable && timeToSkip > 0) {
                    this.skipButton.textContent = `${timeToSkip}`
                }

                if (creative.skippable && timeToSkip <= 0) {
                    this.skipButton.textContent = locales[this.locale]['skip']
                    this.skipButton.disabled = false

                    if (!skipButtonAppearLogged) {
                        this.logEvent({
                            event: 'direct_ad_event',
                            eventName: 'direct_ad_skip_button_shown',
                            adPlacement: type,
                            adUnit: this.getAdUnit(type, creative),
                        })
                        skipButtonAppearLogged = true
                    }

                    this.skipButton.addEventListener('click', () => {
                        this.endAd(banner, type, creative)
                    })
                }
            })
        } catch {
            banner.triggerEmpty()
        }
    }

    private async loadVideo(creative: Creative): Promise<void> {
        const streamUrlPrefix = getStreamUrlPrefix(creative.file)

        // check if the browser supports HLS natively
        if (this.videoPlayer.canPlayType('application/vnd.apple.mpegurl') !== '') {
            this.videoPlayer.src = `${streamUrlPrefix}/video.m3u8`
            this.videoPlayer.load()
            return
        }

        const shaka = await import('shaka-player/dist/shaka-player.dash.js')

        this.shakaPlayerRef = new shaka.Player()
        this.shakaPlayerRef.configure({
            abr: {
                restrictToElementSize: true,
            },
            manifest: {
                disableAudio: this.isMuted,
            },
            streaming: {
                bufferingGoal: 3,
            },
        })
        this.shakaPlayerRef.attach(this.videoPlayer)
        try {
            await this.shakaPlayerRef.load(`${streamUrlPrefix}/video.mpd`, 0, 'application/dash+xml')
        } catch {
            // ignore loading errors
        }
    }

    private async resetPlayer() {
        try {
            await this.shakaPlayerRef?.destroy()
            this.shakaPlayerRef = null
        } catch {
            // ignore destroy errors
        }

        // shaka removes the <source> element from the <video> tag
        if (!this.videoPlayer.querySelector('source')) {
            const source = document.createElement('source')
            source.src = this.sampleVideoSrc
            this.videoPlayer.appendChild(source)
        }

        // this is a hack to force the player to disappear from the iPhone Control Center
        // (cloning the <video> element and using navigator.mediaSession didn't work).
        // the idea is to set src to an empty string so the player vanishes, but that breaks play() in checkAutoplaySupport.
        // so we remove the src attribute, but if we do that immediately, the Control Center player remains visible.
        // also, calling load() right after removing src causes the sample-video (<source>) to be reloaded,
        // so we defer the src removal and call load() in checkAutoplaySupport instead.
        // the 1-second timeout was determined empirically; shorter delays aren't always reliable.
        const TIMEOUT_FOR_RESET = 1000
        this.videoPlayer.src = ''
        this.videoPlayer.load()

        setTimeout(() => {
            this.videoPlayer.removeAttribute('src')
        }, TIMEOUT_FOR_RESET)
    }

    private endAd(banner: OutOfPageBanner, type: OutOfPageAdType, creative: Creative): void {
        banner.watchedDuration = Math.round(this.videoPlayer.currentTime)
        banner.instreamDuration = Math.round(this.videoPlayer.duration)

        this.videoWrapper.style.removeProperty('opacity')
        this.videoWrapper.style.removeProperty('pointer-events')
        this.videoWrapper.style.removeProperty('z-index')

        if (this.learnMoreClickHandler) {
            this.learnMoreButton.removeEventListener('click', this.learnMoreClickHandler)
            this.learnMoreClickHandler = undefined
        }

        if (this.videoClickHandler) {
            this.videoPlayer.removeEventListener('click', this.videoClickHandler)
            this.videoClickHandler = undefined
        }

        this.resetPlayer()

        if (type === 'rewarded') {
            banner.triggerRewarded({})
        }

        this.logEvent({
            event: 'direct_ad_event',
            eventName: 'direct_ad_close',
            adPlacement: type,
            adUnit: this.getAdUnit(type, creative),
            instreamDuration: banner.instreamDuration,
            watchedDuration: banner.watchedDuration,
        })

        banner.triggerClosed()
    }

    async requestAd(type: OutOfPageAdType, _?: number): Promise<OutOfPageBanner> {
        this.logEvent({
            event: 'direct_ad_event',
            eventName: 'direct_ad_request',
            adPlacement: type,
            adUnit: this.getAdUnit(type),
        })

        const banner = new OutOfPageBanner({
            provider: AdProviderNames.DIRECT,
            adUnit: this.getAdUnit(type),
        })

        const creative = this.getCreative(type)

        if (!creative || creative.format !== 'video') {
            window.setTimeout(() => {
                banner.triggerEmpty()
            }, MICRO_TIMEOUT)
            return banner
        }

        const support = await this.checkAutoplaySupport()

        await this.loadVideo(creative)

        if (support === 'muted') {
            this.videoPlayer.muted = true
            this.videoPlayer.setAttribute('muted', '1')
        }

        if (support === 'no') {
            this.wrapperControls.switchOverlayVisibility(true)
            this.wrapperControls.setContinueAdHandler(async () => {
                await this.playVideo(banner, creative, type)
            })

            return banner
        }

        this.wrapperControls.setContinueAdHandler(async () => {
            this.wrapperControls.switchOverlayVisibility(false)
            await this.videoPlayer.play()
        })

        this.videoWrapper.style.opacity = '1'
        this.videoWrapper.style.pointerEvents = 'auto'
        this.videoWrapper.style.zIndex = '2147483647' // max z-index to be on top of everything

        window.setTimeout(() => {
            banner.triggerReady(async () => {
                await this.playVideo(banner, creative, type)
            })
        }, MICRO_TIMEOUT)

        return banner
    }

    private async checkAutoplaySupport(): Promise<InstreamSupport> {
        this.videoPlayer.load()

        this.videoPlayer.volume = 1
        this.isMuted = false
        this.videoPlayer.removeAttribute('muted')

        try {
            await this.videoPlayer.play()
            this.videoPlayer.pause()
            return 'full'
        } catch {
            return this.checkMutedAutoplaySupport()
        }
    }

    private async checkMutedAutoplaySupport(): Promise<InstreamSupport> {
        this.videoPlayer.volume = 0
        this.videoPlayer.muted = true

        try {
            await this.videoPlayer.play()
            this.videoPlayer.pause()
            this.isMuted = true
            this.videoPlayer.setAttribute('muted', '1')
            return 'muted'
        } catch {
            return 'no'
        }
    }
}
